import React from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { GRID_SERVICE_KEYS } from 'components/AgGrid/helpers';
import {
  IS_OPEN_CONTEXT_MENU_CLASS,
  ROW_HAS_ERROR,
  ROW_HAS_WARNING,
  ROW_PUBLISH_SCHEDULING,
} from 'components/AgGrid/Documents/agGridThemeClasses';

const {
  PREVIEW_CHECKBOX_COLUMN_NAME,
  PREVIEW_BTN_COLUMN_NAME,
  COLUMNS_VISIBILITY_MENU,
  CONTEXT_MENU_COLUMN_NAME,
  COLUMNS_LINKED_ICON,
} = GRID_SERVICE_KEYS;

const AgGridDocumentsStyles = () => {
  return (
    <GlobalStyles
      styles={(theme) => {
        const isRtl = theme.direction === 'rtl';
        const LEFT = isRtl ? 'right' : 'left';
        const RIGHT = isRtl ? 'left' : 'right';

        return {
          '.ag-theme-dokka': {
            // Page-size selector sits 50px from the left with the page-nav pushed right
            // (margin-right:auto). Moved here from AgGridGlobalStyles (DA-19459) so only Workspace +
            // JEInsights keep this layout; other grids fall back to AG Grid's default right-aligned
            // paging panel.
            '& .ag-paging-panel .ag-paging-page-size': {
              margin: '0 auto 0 50px',
            },

            '& .ag-overlay': {
              pointerEvents: 'all',

              '& .ag-react-container': {
                justifyContent: 'center',
                // Without this, the wrapper sizes to content and breaks
                // height-based @container queries inside the overlay component.
                height: '100%',
                width: '100%',

                '& .MuiDivider-root': {
                  display: 'none',
                },
              },
            },

            '& .ag-selection-checkbox': {
              marginLeft: theme.spacing(1),
              marginRight: 0,
            },

            [`& .ag-icon.ag-icon-${isRtl ? 'first' : 'last'}`]: {
              display: 'none',
            },

            // ======== STYLES FOR HEADER PINNED COLUMNS
            '& .ag-header': {
              [`& .ag-pinned-${LEFT}-header`]: {
                '& .ag-header-cell': {
                  '&:nth-child(2)': {
                    borderRadius: '8px 0 0 8px',
                    border: '1px solid var(--ag-border-color)',
                    borderRight: 'none',
                  },
                },
              },

              [`& .ag-pinned-${RIGHT}-header`]: {
                border: 'none',
              },
            },

            '& .ag-header-cell': {
              [`&.${COLUMNS_VISIBILITY_MENU}`]: {
                padding: theme.spacing(0, 0.5),
                borderRadius: '0 8px 8px 0',
                border: '1px solid var(--ag-border-color) !important',
              },

              [`&.${CONTEXT_MENU_COLUMN_NAME}`]: {
                display: 'none',
              },

              [`&.${PREVIEW_CHECKBOX_COLUMN_NAME}`]: {
                flexDirection: 'row-reverse',
              },

              '& .ag-header-select-all': {
                margin: 0,
              },
            },

            '& .ag-cell': {
              [`&.${COLUMNS_VISIBILITY_MENU}`]: {
                borderRight: '1px solid var(--ag-border-color) !important',
                borderRadius: '0 8px 8px 0',
              },

              [`&.${CONTEXT_MENU_COLUMN_NAME}`]: {
                opacity: 0,
                border: 0,
                backgroundColor: 'transparent',

                // context menu icon stay visible when menu is open
                [`&.${IS_OPEN_CONTEXT_MENU_CLASS}`]: {
                  opacity: '1!important',
                },
              },

              [`&.${PREVIEW_CHECKBOX_COLUMN_NAME}`]: {
                paddingLeft: 8,

                '& > .ag-cell-wrapper': {
                  flexDirection: 'row-reverse',
                },
              },

              [`&[col-id="${PREVIEW_CHECKBOX_COLUMN_NAME}"],
            &[col-id="${PREVIEW_BTN_COLUMN_NAME}"],
            &[col-id="${CONTEXT_MENU_COLUMN_NAME}"],
            &[col-id="${COLUMNS_LINKED_ICON}"],
            &[col-id="${COLUMNS_VISIBILITY_MENU}"]`]: {
                '& .ag-range-handle': {
                  width: 0,
                },
              },
            },

            '& .ag-cell-expandable': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 2,
            },

            '& .ag-row-group-expanded': {
              [`& .${PREVIEW_CHECKBOX_COLUMN_NAME}`]: {
                paddingLeft: 0,

                '& > .ag-cell-wrapper': {
                  borderLeft: `3px solid ${theme.palette.common.black}`,
                  borderRadius: '4px 0 0 4px',
                },
              },
            },

            // // ======== STYLES FOR ROW
            '& .ag-group-contracted, .ag-group-expanded': {
              margin: '0 !important',
            },

            '& .ag-row': {
              '&.ag-row-level-1': {
                backgroundColor: 'var(--ag-background-color) !important',
                width: 'calc(100% - 70px)',
                maxHeight: 390,
                marginLeft: 60,
                borderLeft: `3px solid ${theme.palette.common.black}`,
                outline: '1px solid var(--ag-border-color)',
                borderRadius: 8,
              },
              [`&.${ROW_HAS_ERROR}`]: {
                '& .ag-cell': {
                  backgroundColor: '#f4e3e5',
                },
                '&.ag-row-hover .ag-cell': {
                  backgroundColor: '#f3dbdd',
                },
                '&.ag-row-selected .ag-cell': {
                  backgroundColor: '#f3dbdd',
                },
                '& .ag-cell.ag-cell-range-selected:not(.ag-column-hover)': {
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'var(--ag-range-selection-background-color ,rgba(122,134,203,.1)) !important',
                  },
                },
              },

              [`&.${ROW_HAS_WARNING}`]: {
                '& .ag-cell': {
                  backgroundColor: '#f7e9e2',
                },
                '&.ag-row-hover .ag-cell': {
                  backgroundColor: '#f7e4da',
                },
                '&.ag-row-selected .ag-cell': {
                  backgroundColor: '#f7e4da',
                },
                '& .ag-cell.ag-cell-range-selected:not(.ag-column-hover)': {
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'var(--ag-range-selection-background-color ,rgba(122,134,203,.1)) !important',
                  },
                },
              },

              [`&.${ROW_PUBLISH_SCHEDULING}`]: {
                '& .ag-cell': {
                  backgroundColor: '#e7e9ec',
                  color: theme.palette.text.disabled,
                },
                '&.ag-row-hover .ag-cell': {
                  backgroundColor: '#e3e5e7',
                },
                '&.ag-row-selected .ag-cell': {
                  backgroundColor: '#e3e5e7',
                },
                '& .ag-cell.ag-cell-range-selected:not(.ag-column-hover)': {
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'var(--ag-range-selection-background-color ,rgba(122,134,203,.1)) !important',
                  },
                },
              },

              '&.ag-row-hover': {
                '&:before': {
                  display: 'none',
                },

                '& .ag-cell': {
                  [`&.${CONTEXT_MENU_COLUMN_NAME}`]: {
                    opacity: 1,
                    backgroundColor: 'transparent',
                  },
                },
              },
            },

            [`& .ag-pinned-${LEFT}-cols-container, & .ag-pinned-${LEFT}-sticky-top`]: {
              '& .ag-row': {
                '& .ag-cell': {
                  '&:nth-child(2)': {
                    borderRadius: '8px 0 0 8px',
                    border: '1px solid var(--ag-border-color)',
                    borderRight: 'none',
                  },
                },
              },
            },
          },
        };
      }}
    />
  );
};

export default AgGridDocumentsStyles;
